import {DepositActions, DepositActionType} from "./DepositActions";
import {
    DepositContext,
    DepositResult,
    PaymentDialog,
    PaymentMethod,
    RequestWithdrawResponse,
    TransferLimit,
    UserInfo,
    WithdrawContext,
    WithdrawPermitted
} from "../http/protocol";
import {PaymentMehtodsByCurrency, PriorityType} from "../http/Types";
import {combineReducers} from "redux";
import {WithdrawActions, WithdrawActionType} from "./WithdrawActions";
import {UserAction, UserActionTypes} from "../user/UserActions";
import {PaymentActions, PaymentActionType} from "./PaymentsActions";

export enum PaymentStateStatus {
    NONE = "NONE",
    DEPOSIT_INITIATED = "DEPOSIT_INITIATED",
    DEPOSIT_INITIALIZED = "DEPOSIT_INITIALIZED",
    DEPOSIT_ERROR_INITIALIZING = "DEPOSIT_ERROR_INITIALIZING"
}

export enum WithdrawStateStatus {
    NONE = "NONE",
    WITHDRAW_INITIATED = "WITHDRAW_INITIATED",
    WITHDRAW_INITIALIZED = "WITHDRAW_INITIALIZED",
    WITHDRAW_ERROR_INITIALIZING = "WITHDRAW_ERROR_INITIALIZING"
}
export enum DepositPhase {
    ERROR = "ERROR",
    CHOOSE_METHOD = "CHOOSE_METHOD",
    CHOOSE_AMOUNT = "CHOOSE_AMOUNT",
    PROVIDER_CONTENT = "PROVIDER_CONTENT",
    DEPOSIT_RESULT = "DEPOSIT_RESULT"
}

export enum WithdrawPhase {
    ERROR = "ERROR",
    CHOOSE_METHOD = "CHOOSE_METHOD",
    CHOOSE_PROVIDER = "CHOOSE_PROVIDER",
    CHOOSE_ADDRESS = "CHOOSE_ADDRESS",
    CHOOSE_AMOUNT = "CHOOSE_AMOUNT",
    CONFIRM_WITHDRAW = "CONFIRM_WITHDRAW",
    WITHDRAW_RESPONSE = "WITHDRAW_RESPONSE",
    KYC_VERIFICATION = "KYC_VERIFICATION",
    REQUEST_DENIED = "REQUEST_DENIED"
}
export interface MethodAndWithdrawContext {
    method: PaymentMethod;
    context : WithdrawContext;
    currencyCode : string;
}
export interface MethodAndDepositContext {
    method: PaymentMethod;
    context : DepositContext;
    currencyCode : string;
}
export interface PreferredMethod {
    currencyCode: string;
    method: string;
}
export interface DepositState {
    status? : PaymentStateStatus;
    phase? : DepositPhase;
    depositDialog? : PaymentDialog;
    methods? : PaymentMehtodsByCurrency
    activeCurrency? : string;
    activeMethod? : PaymentMethod;
    amount?: string;
    showPossibleErrors? : boolean;
    amountValid?: boolean;
    amountPresets? : string[];
    useAsStore?: boolean;
    depositResult?: DepositResult;
    depositContext?: DepositContext;
    depositContextStatus? : ContextStatus;
    sendingPayment?: boolean;
    sendingPaymentFailed?:boolean;
    error?:string;
    preferredMethods? : PreferredMethod[],
    group? : string;
    address?: string;
}

export enum ContextStatus {
    IN_PROGRESS = "IN_PROGRESS",
    OK = "OK",
    ERROR = "ERROR"
}

export enum CashierSection {
    DEPOSIT = "DEPOSIT",
    WITHDRAW = "WITHDRAW",
    BUY_CRYPTO = "BUY_CRYPTO",
    TIP = "TIP",
    NONE = "NONE"
}
export interface CashierState {
    showCashierModal? : boolean;
    section? : CashierSection;
}
export interface WithdrawState {
    status? : WithdrawStateStatus;
    phase? : WithdrawPhase;
    withdrawResponse? : RequestWithdrawResponse;
    methods? : PaymentMehtodsByCurrency;
    activeCurrency? : string;
    activeMethod? : PaymentMethod;
    amount?: string;
    amountValid?: boolean;
    recipientAddress?:string;
    destinationTag?: string;
    notesTag?: string;
    withdrawContext? : WithdrawContext;
    withdrawContextStatus? : ContextStatus;
    showPossibleErrors?: boolean;
    priority? : PriorityType;
    preferredMethods? : PreferredMethod[];
    orderCustom? : any;
    group? : string;
}

export interface PaymentsState {
    deposit : DepositState;
    withdraw : WithdrawState;
    cashier : CashierState;
}

const deposit : DepositState = {
    phase : DepositPhase.CHOOSE_METHOD,
    status : PaymentStateStatus.NONE,
    amount : "",
    showPossibleErrors : false,
    amountValid : false,
    depositContextStatus : ContextStatus.OK,
    amountPresets : ["500", "1000", "2000"],
    preferredMethods : []
};

const withdraw : WithdrawState = {
    phase : WithdrawPhase.CHOOSE_METHOD,
    status : WithdrawStateStatus.NONE,
    amount : "",
    amountValid : false,
    withdrawContextStatus : ContextStatus.OK,
    showPossibleErrors: false,
    preferredMethods : [],
    orderCustom : undefined,
};

function extractPreferredDepositMethods(response: UserInfo) {

    if(response.userSettings) {
        let preferredProviders : PreferredMethod[] = []
        Object.keys(response.userSettings).forEach( (k : string) => {
            if(k.startsWith("last_deposit_provider_")) {
                let currencyCode = k.replace("last_deposit_provider_","")
                if(currencyCode && response.userSettings?.[k]) {
                    preferredProviders.push({ currencyCode : currencyCode, method :  response.userSettings[k]});
                }
            }
        })
        return preferredProviders;
    }
    return [];
}
function extractPreferredWithdrawMethods(response: UserInfo) {

    if(response.userSettings) {
        let preferredProviders : PreferredMethod[] = []
        Object.keys(response.userSettings).forEach( (k : string) => {
            if(k.startsWith("last_withdraw_provider_")) {
                let currencyCode = k.replace("last_withdraw_provider_","")
                if(currencyCode && response.userSettings?.[k]) {
                    preferredProviders.push({ currencyCode : currencyCode, method :  response.userSettings[k]});
                }
            }
        })
        return preferredProviders;
    }
    return [];
}
export function depositReducer(state : DepositState = deposit, action : DepositActions|UserAction|PaymentActions) : DepositState {

    switch (action.type) {
        case PaymentActionType.SELECT_GROUP:
            return {...state, group : action.group };
        case UserActionTypes.USER_INFO_RESPONSE:
            if(state.preferredMethods?.length  == 0) {
                return {...state, preferredMethods : extractPreferredDepositMethods(action.info)}
            }
            return state;
        case UserActionTypes.SET_ACTIVE_CURRENCY:
            return handleProvidersResponse({...state, activeCurrency : undefined}, state.methods);
        case DepositActionType.RESET_DEPOSIT_STATUS_AND_CONTEXT:
            return {...state, status : PaymentStateStatus.NONE, depositContextStatus : ContextStatus.OK, activeMethod : undefined};
        case DepositActionType.INIT_DEPOSIT_REQUEST:
            return  {...state, status : PaymentStateStatus.DEPOSIT_INITIATED, phase : DepositPhase.PROVIDER_CONTENT, depositDialog : undefined, depositResult : undefined, sendingPayment : false, sendingPaymentFailed : false};
        case DepositActionType.INIT_DEPOSIT_RESPONSE:
            return  {...state, status : PaymentStateStatus.DEPOSIT_INITIALIZED, phase : action.instantDeposit ? DepositPhase.DEPOSIT_RESULT : DepositPhase.PROVIDER_CONTENT, depositDialog : action.dialog, amount : "", amountValid : false, showPossibleErrors: false };
        case DepositActionType.INIT_DEPOSIT_RESULT:
            return  {...state, phase : DepositPhase.DEPOSIT_RESULT, depositResult : action.depositResult };
        case DepositActionType.SHOW_POSSIBLE_DEPOSIT_ERRORS:
            return {...state, showPossibleErrors : true};
        case DepositActionType.INIT_DEPOSIT_ERROR:
            return  {...state, status : PaymentStateStatus.DEPOSIT_ERROR_INITIALIZING, depositDialog : undefined, error:action.error.toString()};
        case PaymentActionType.PROVIDER_RESPONSE:
            return handleProvidersResponse(state,action.methods);
        case PaymentActionType.PROVIDER_ERROR:
            return {...state, phase : DepositPhase.ERROR };
        case DepositActionType.DEPOSIT_CONTEXT_REQUEST:
            return {...state,  depositContextStatus : ContextStatus.IN_PROGRESS, depositContext : undefined, activeCurrency : undefined };
        case DepositActionType.DEPOSIT_CONTEXT_ERROR:
            return { ...state, depositContextStatus : ContextStatus.ERROR } ;
        case DepositActionType.DEPOSIT_CONTEXT_RESPONSE:
            return handleSelectMethod(state, action.method, action.currencyCode, action.context);
        case DepositActionType.RESET_DEPOSIT_CONTEXT:
            return {...state, depositDialog : undefined, depositContext : undefined, depositContextStatus : ContextStatus.OK};
        case DepositActionType.UPDATE_AMOUNT:
            return {...state, amount : action.amount, amountValid : validateDepositAmount(action.amount, state.depositContext)};
        case DepositActionType.UPDATE_ADDRESS:
            return {...state, address : action.address};
        case DepositActionType.SET_USE_AS_STORE:
            return {...state, useAsStore: action.useAsStore}
        case DepositActionType.SET_SENDING_PAYMENT:
            return {...state, sendingPayment: action.sendingPayment}
        case DepositActionType.SET_SENDING_PAYMENT_FAILED:
            return {...state, sendingPaymentFailed: action.sendingPaymentFailed}
        case DepositActionType.SHOW_CASHIER:
            return handleProvidersResponse(state,state.methods);
        case PaymentActionType.RESET:
            return handleProvidersResponse(state,state.methods, action.section, action.removePreferredForCurrencyCode);
        case DepositActionType.UPDATE_PHASE:
            return {...state, phase : action.phase};
        case DepositActionType.RESET_ERROR:
            return {...state, error : undefined};
        default:
            return state;
    }
}
export function withdrawReducer(state : WithdrawState = withdraw, action : WithdrawActions|UserAction|PaymentActions|DepositActions) : WithdrawState {
    switch (action.type) {
        case PaymentActionType.SELECT_GROUP:
            return {...state, group : action.group };
        case UserActionTypes.USER_INFO_RESPONSE:
            if(state.preferredMethods?.length  == 0) {
                return {...state, preferredMethods : extractPreferredWithdrawMethods(action.info)}
            }
            return state;
        case WithdrawActionType.WITHDRAW_CONTEXT_REQUEST:
            return {...state,  withdrawContextStatus : ContextStatus.IN_PROGRESS, activeCurrency: undefined, activeMethod: undefined };
        case WithdrawActionType.WITHDRAW_CONTEXT_ERROR:
            return { ...state, withdrawContextStatus : ContextStatus.ERROR } ;
        case UserActionTypes.SET_ACTIVE_CURRENCY:
            return handleProviderWithdrawResponse({...state, activeCurrency : undefined}, state.methods);
        case WithdrawActionType.INIT_WITHDRAW_REQUEST:
            return  {...state, status : WithdrawStateStatus.WITHDRAW_INITIATED, withdrawResponse : undefined};
        case WithdrawActionType.INIT_WITHDRAW_RESPONSE:
            if (action.response.accepted) {
                return  {...state, status : WithdrawStateStatus.WITHDRAW_INITIALIZED, phase :  WithdrawPhase.WITHDRAW_RESPONSE, withdrawResponse : action.response };
            } else {
                return  {...state, status : WithdrawStateStatus.WITHDRAW_INITIALIZED, phase :  WithdrawPhase.REQUEST_DENIED, withdrawResponse : action.response };
            }
        case WithdrawActionType.INIT_WITHDRAW_ERROR:
            let phase = WithdrawPhase.ERROR;
            if((action.error as any).response.status === 406) {
                phase = WithdrawPhase.REQUEST_DENIED;
            }
            return  {...state, status : WithdrawStateStatus.WITHDRAW_ERROR_INITIALIZING, withdrawResponse : undefined, phase: phase};
        case PaymentActionType.PROVIDER_RESPONSE:
            return handleProviderWithdrawResponse(state,action.methods);
        case PaymentActionType.PROVIDER_ERROR:
            return {...state, phase : WithdrawPhase.ERROR };
        case WithdrawActionType.WITHDRAW_CONTEXT_RESPONSE:
            return handleSelectWithdrawMethod(state, action.method, action.context, action.currencyCode);
        case WithdrawActionType.UPDATE_WITHDRAW_ADDRESS:
            return {...state, recipientAddress: action.recipientAddress};
        case WithdrawActionType.UPDATE_DESTINATION_TAG:
            return {...state, destinationTag: action.destinationTag};
        case WithdrawActionType.UPDATE_NOTES_TAG:
            return {...state, notesTag: action.notesTag};
        case WithdrawActionType.UPDATE_ORDER_CUSTOM:
            return {...state, orderCustom : action.custom };
        case WithdrawActionType.RESET_WITHDRAW_CONTEXT:
            return {...state, withdrawContext : undefined, withdrawContextStatus : ContextStatus.OK, activeMethod : undefined};
        case WithdrawActionType.SET_AMOUNT:
            return  {...state, phase : WithdrawPhase.CHOOSE_AMOUNT, withdrawResponse : undefined};
        case WithdrawActionType.CONFIRM_WITHDRAW:
            return {...state, phase : WithdrawPhase.CONFIRM_WITHDRAW, priority : action.priority};
        case WithdrawActionType.UPDATE_AMOUNT:
            return {...state, amount : action.amount, amountValid : validateAmount(action.amount, action.limit)};
        case DepositActionType.SHOW_CASHIER:
            return handleProviderWithdrawResponse(state,state.methods);
        case PaymentActionType.RESET:
            return handleProviderWithdrawResponse(state,state.methods, action.section, action.removePreferredForCurrencyCode);
        case WithdrawActionType.SHOW_POSSIBLE_WITHDRAW_ERRORS:
            return {...state, showPossibleErrors : true};
        case WithdrawActionType.UPDATE_WITHDRAW_FORM_DATA:
            return {...state, ...action.payload};
        default:
            return state;
    }
}
const cashier : CashierState = { showCashierModal : false, section : CashierSection.DEPOSIT };
export function cashierReducer(state : CashierState = cashier, action : DepositActions) : CashierState {
    switch (action.type) {
        case DepositActionType.SHOW_CASHIER:
            return {...state, showCashierModal : action.show, section : action.section ? action.section : CashierSection.DEPOSIT};
        default:
            return state;
    }
}
export function validateDepositAmount(amount? : string, context? : DepositContext) {
    if(amount && amount !="" && Number(amount) > 0) {
        if(context && context.minDeposit !== undefined && context.maxDeposit !== undefined) {
            let a = Number(amount);
            if(a >= context.minDeposit && a <= context.maxDeposit) {
                return true;
            }
            return false;
        } else {
            return true;
        }
    }
    return false;
}

export function validateAmount(amount? : string, limit?:TransferLimit) {
    if(amount && amount !="" && Number(amount) > 0) {
        if(limit && limit.minWithdrawAmount !== undefined && limit.maxWithdrawAmount !== undefined) {
            let a = Number(amount);
            if(a >= limit.minWithdrawAmount && a <= limit.maxWithdrawAmount) {
                return true;
            }
            return false;
        } else {
            return true;
        }
    }
    return false;
}
export function handleSelectMethod(state : DepositState, method : PaymentMethod, currencyCode : string, context : DepositContext) {
    let preferred = removePreferredMethodForCurrency(state.preferredMethods, currencyCode) ?? [];
    method.code && preferred.push({ method : method.code, currencyCode : currencyCode});


    // if (method.addressRequired == true && (state.useAsStore === undefined)) {
    //     return  {
    //         ...state,
    //         activeMethod : method,
    //         preferredMethods : preferred,
    //         activeCurrency : currencyCode,
    //         phase : DepositPhase.CHOOSE_ADDRESS,
    //         depositContext : context,
    //         depositContextStatus : ContextStatus.OK
    //     };
    // } else
    if (method.amountRequired == true && (state.useAsStore === undefined)) {
        return  {
            ...state,
            activeMethod : method,
            preferredMethods : preferred,
            activeCurrency : currencyCode,
            phase : DepositPhase.CHOOSE_AMOUNT,
            depositContext : context,
            depositContextStatus : ContextStatus.OK
        };
    } else {
        return  {
            ...state,
            preferredMethods : preferred,
            activeMethod : method,
            activeCurrency : currencyCode,
            phase : DepositPhase.PROVIDER_CONTENT,
            depositContext : context,
            depositContextStatus : ContextStatus.OK
        };
    }
}

export function handleSelectWithdrawMethod(state: WithdrawState, method : PaymentMethod, context: WithdrawContext, currencyCode : string) {

    let preferred = removePreferredMethodForCurrency(state.preferredMethods, currencyCode) ?? [];
    method.code && preferred.push({ method : method.code, currencyCode : currencyCode});
    //Change when new value added to paymentMethod that identifies crypto
    if(context.permitted != WithdrawPermitted.YES) {
        return {
            ...state,
            withdrawContext: context,
            phase: WithdrawPhase.CHOOSE_METHOD,
            orderCustom : undefined,
            withdrawContextStatus: ContextStatus.ERROR,
            preferredMethods : preferred
        }
    }
    if(method && method.achBankTransfer || (method.code == 'ACH')) {
        return {...state,
            activeMethod : method,
            withdrawContext : context,
            withdrawContextStatus : ContextStatus.OK,
            phase : WithdrawPhase.CHOOSE_AMOUNT,
            activeCurrency : currencyCode,
            priority : undefined,
            preferredMethods : preferred
        };
    }
    if(method && method.code === "maya-mini-app") {
        return {
            ...state,
            activeMethod : method,
            withdrawContext : context,
            withdrawContextStatus : ContextStatus.OK,
            phase : WithdrawPhase.CHOOSE_AMOUNT,
            activeCurrency : currencyCode,
            priority : undefined,
            preferredMethods : preferred
        };
    } else if(method && method.amountRequired) {

        return {
            ...state,
            withdrawContext: context,
            orderCustom : undefined,
            withdrawContextStatus: ContextStatus.OK,
            activeMethod : method,
            phase : WithdrawPhase.CHOOSE_PROVIDER,
            activeCurrency: currencyCode,
            priority : undefined,
            preferredMethods : preferred
        };
    } else {

        return {
            ...state,
            activeMethod : method,
            withdrawContext: context,
            orderCustom : undefined,
            withdrawContextStatus: ContextStatus.OK,
            phase : WithdrawPhase.CHOOSE_ADDRESS,
            activeCurrency: currencyCode,
            priority : undefined,
            preferredMethods : preferred
        };
    }
}

function removePreferredMethodForCurrency(preferredMethods?: PreferredMethod[], removePreferredForCurrencyCode?: string) {
    if(!preferredMethods || !removePreferredForCurrencyCode) {
        return preferredMethods;
    }
    return preferredMethods.filter(pm => pm.currencyCode != removePreferredForCurrencyCode);
}

export function handleProvidersResponse(state : DepositState, methods? : PaymentMehtodsByCurrency, section?: CashierSection, removePreferredForCurrencyCode? : string) : DepositState {
    if(!methods) {
        return {...state, phase : DepositPhase.ERROR }
    }
    //skip choose provider step if only one method for currency
    let preferredMethods = state.preferredMethods;
    if(section == CashierSection.DEPOSIT) {
        preferredMethods = removePreferredMethodForCurrency(state.preferredMethods, removePreferredForCurrencyCode);
    }
    let newState = {
        ...state,
        activeMethod : undefined,
        methods : methods,
        preferredMethods : preferredMethods,
        activeCurrency : undefined,
        status : PaymentStateStatus.NONE,
        depositDialog : undefined,
        depositContext : undefined,
        phase : DepositPhase.CHOOSE_METHOD,
        group : undefined
    };
    return  newState;


}

export function handleProviderWithdrawResponse(state : WithdrawState, methods? : PaymentMehtodsByCurrency, section? : CashierSection, removePreferredForCurrencyCode? : string) : WithdrawState {
    let preferred = state.preferredMethods;
    if(section == CashierSection.WITHDRAW) {
        preferred = removePreferredMethodForCurrency(preferred, removePreferredForCurrencyCode) ?? [];
    }

    if(!methods) {
        return {...state, phase : WithdrawPhase.ERROR }
    }

    return  {...state,
        methods : methods,
        preferredMethods : preferred,
        activeCurrency : undefined,
        status : WithdrawStateStatus.NONE,
        withdrawContextStatus : ContextStatus.OK,
        withdrawResponse : undefined,
        withdrawContext : undefined,
        activeMethod : undefined,
        phase : WithdrawPhase.CHOOSE_METHOD,
        priority : undefined,
        group : undefined
    };


}

export function getCurrencies(methods: PaymentMehtodsByCurrency) {
    let currencies = [];
    for(let x in methods) {
        currencies.push(x);
    }
    return currencies;
}

export const paymentsReducer =  combineReducers({
    deposit : depositReducer,
    withdraw : withdrawReducer,
    cashier : cashierReducer
});